import { useEffect, useRef, useState } from 'react';
import type { LiveAbsender, LiveVomServer, LiveVonClient } from '@beholder/shared';

type Hoerer = (daten: unknown, von?: string) => void;
type FluechtigHoerer = (daten: unknown, von: LiveAbsender) => void;

/**
 * Eine gemeinsame WebSocket-Verbindung für alle Live-Kanäle.
 * Verbindet sich bei Abbruch selbst neu und abonniert alle Kanäle erneut.
 */
class LiveVerbindung {
  private ws: WebSocket | null = null;
  private hoerer = new Map<string, Set<Hoerer>>();
  private fluechtigHoerer = new Map<string, Set<FluechtigHoerer>>();
  private warteschlange: LiveVonClient[] = [];
  private versuch = 0;
  private zustandHoerer = new Set<(verbunden: boolean) => void>();
  verbunden = false;

  private verbinde() {
    if (this.ws && this.ws.readyState <= 1) return;
    const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/live`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.versuch = 0;
      this.setzeVerbunden(true);
      for (const kanal of this.hoerer.keys()) this.roh({ typ: 'abo', kanal });
      for (const n of this.warteschlange.splice(0)) this.roh(n);
    };
    ws.onmessage = (e) => {
      let n: LiveVomServer;
      try {
        n = JSON.parse(String(e.data));
      } catch {
        return;
      }
      if (n.typ === 'nachricht') this.hoerer.get(n.kanal)?.forEach((h) => h(n.daten, n.von));
      if (n.typ === 'fluechtig') this.fluechtigHoerer.get(n.kanal)?.forEach((h) => h(n.daten, n.von));
      if (n.typ === 'fehler' && n.kanal && n.text === 'rechte-geaendert') {
        // Rechte wurden geändert: neu abonnieren (Server prüft erneut)
        if (this.hoerer.has(n.kanal)) this.roh({ typ: 'abo', kanal: n.kanal });
        window.dispatchEvent(new CustomEvent('beholder:rechte'));
      }
    };
    ws.onclose = () => {
      this.setzeVerbunden(false);
      if (this.hoerer.size === 0) return;
      const pause = Math.min(30_000, 500 * 2 ** this.versuch++);
      setTimeout(() => this.verbinde(), pause);
    };
  }

  private setzeVerbunden(v: boolean) {
    this.verbunden = v;
    this.zustandHoerer.forEach((h) => h(v));
  }

  private roh(n: LiveVonClient) {
    if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(n));
    else {
      if (n.typ === 'senden') this.warteschlange.push(n);
      this.verbinde();
    }
  }

  abonnieren(kanal: string, h: Hoerer): () => void {
    let set = this.hoerer.get(kanal);
    if (!set) {
      set = new Set();
      this.hoerer.set(kanal, set);
      this.roh({ typ: 'abo', kanal });
    }
    set.add(h);
    return () => {
      set!.delete(h);
      if (set!.size === 0) {
        this.hoerer.delete(kanal);
        this.roh({ typ: 'abo-ende', kanal });
      }
    };
  }

  senden(kanal: string, daten: unknown) {
    this.roh({ typ: 'senden', kanal, daten });
  }

  /** Flüchtig an die anderen auf dem Kanal (z. B. Zeiger) – auch mit Leserecht, wird nicht gespeichert, geht bei Abbruch verloren. */
  fluechtig(kanal: string, daten: unknown) {
    if (this.ws?.readyState === 1 && this.hoerer.has(kanal)) this.ws.send(JSON.stringify({ typ: 'fluechtig', kanal, daten } satisfies LiveVonClient));
  }

  /** Flüchtige Nachrichten anderer empfangen; der Kanal muss zusätzlich mit abonnieren()/useLive abonniert sein. */
  beiFluechtig(kanal: string, h: FluechtigHoerer): () => void {
    let set = this.fluechtigHoerer.get(kanal);
    if (!set) {
      set = new Set();
      this.fluechtigHoerer.set(kanal, set);
    }
    set.add(h);
    return () => {
      set!.delete(h);
      if (set!.size === 0) this.fluechtigHoerer.delete(kanal);
    };
  }

  beiZustand(h: (verbunden: boolean) => void): () => void {
    this.zustandHoerer.add(h);
    return () => this.zustandHoerer.delete(h);
  }
}

export const live = new LiveVerbindung();

/** React: Kanal abonnieren, solange die Komponente sichtbar ist. */
export function useLive(kanal: string | null, beiNachricht: Hoerer) {
  const ref = useRef(beiNachricht);
  ref.current = beiNachricht;
  useEffect(() => {
    if (!kanal) return;
    return live.abonnieren(kanal, (d, v) => ref.current(d, v));
  }, [kanal]);
}

/** React: flüchtige Nachrichten (Zeiger o. Ä.) auf einem Kanal empfangen. Kanal zusätzlich mit useLive abonnieren. */
export function useFluechtig(kanal: string | null, beiNachricht: FluechtigHoerer) {
  const ref = useRef(beiNachricht);
  ref.current = beiNachricht;
  useEffect(() => {
    if (!kanal) return;
    return live.beiFluechtig(kanal, (d, v) => ref.current(d, v));
  }, [kanal]);
}

export function useLiveVerbunden(): boolean {
  const [v, setV] = useState(live.verbunden);
  useEffect(() => live.beiZustand(setV), []);
  return v;
}
